import type { RfqExcelImportPreview, RfqExcelItem } from './rfq-excel.types';
import type {
  RfqPartFinderItemResolution,
  RfqPartFinderResolveStatus,
} from './rfq-import-partfinder-resolve';

export type RfqImportReviewFilter = 'all' | 'matched' | 'needs_review';

export interface RfqPartFinderStatusDisplay {
  status: RfqPartFinderResolveStatus;
  label: string;
  shortLabel: string;
  detail: string;
  tone: 'success' | 'warning' | 'neutral';
  needsReview: boolean;
}

export interface RfqPartFinderReviewSummary {
  importedItems: number;
  matched: number;
  unresolved: number;
  ambiguous: number;
  notSearched: number;
  needsReviewCount: number;
  manualReviewRecommended: boolean;
}

export interface RfqImportReviewItemRow {
  item: RfqExcelItem;
  index: number;
  resolution: RfqPartFinderItemResolution | undefined;
  statusDisplay: RfqPartFinderStatusDisplay;
  /** Raw resolver warnings (kept for association/tests). */
  itemWarnings: string[];
  /** User-facing reasons: no "Item N:" prefix, no internal ids. */
  reasons: string[];
}

const STATUS_DISPLAY: Record<RfqPartFinderResolveStatus, Omit<RfqPartFinderStatusDisplay, 'status'>> =
  {
    matched: {
      label: 'Matched',
      shortLabel: 'MATCHED',
      detail: 'PartFinder match',
      tone: 'success',
      needsReview: false,
    },
    unresolved: {
      label: 'Unresolved',
      shortLabel: 'UNRESOLVED',
      detail: 'No exact PartFinder match',
      tone: 'warning',
      needsReview: true,
    },
    ambiguous: {
      label: 'Ambiguous',
      shortLabel: 'AMBIGUOUS',
      detail: 'Multiple PartFinder matches',
      tone: 'warning',
      needsReview: true,
    },
    not_searched: {
      label: 'Not searched',
      shortLabel: 'NOT SEARCHED',
      detail: 'No Part No / IMPA provided',
      tone: 'neutral',
      needsReview: false,
    },
  };

export function getPartFinderStatusDisplay(
  status: RfqPartFinderResolveStatus | undefined
): RfqPartFinderStatusDisplay {
  const resolved: RfqPartFinderResolveStatus = status ?? 'not_searched';
  return { status: resolved, ...STATUS_DISPLAY[resolved] };
}

export function buildPartFinderReviewSummary(
  resolutions: RfqPartFinderItemResolution[] | undefined,
  itemCount?: number
): RfqPartFinderReviewSummary {
  const list = resolutions ?? [];
  const matched = list.filter((r) => r.status === 'matched').length;
  const unresolved = list.filter((r) => r.status === 'unresolved').length;
  const ambiguous = list.filter((r) => r.status === 'ambiguous').length;
  const notSearched = list.filter((r) => r.status === 'not_searched').length;
  const needsReviewCount = unresolved + ambiguous;
  return {
    importedItems: itemCount ?? list.length,
    matched,
    unresolved,
    ambiguous,
    notSearched,
    needsReviewCount,
    manualReviewRecommended: needsReviewCount > 0,
  };
}

/**
 * Split top-level warnings into header/master-data vs PartFinder item-level.
 * Item warnings are also available on each resolution; this avoids dumping them
 * only into a generic list without row context.
 */
export function partitionImportWarnings(
  warnings: string[] | undefined,
  resolutions: RfqPartFinderItemResolution[] | undefined
): { headerWarnings: string[]; partfinderWarningSet: Set<string> } {
  const partfinderWarningSet = new Set(
    (resolutions ?? []).flatMap((r) => r.warnings ?? [])
  );
  const headerWarnings = (warnings ?? []).filter((w) => !partfinderWarningSet.has(w));
  return { headerWarnings, partfinderWarningSet };
}

export function buildImportReviewItemRows(
  items: RfqExcelItem[],
  resolutions: RfqPartFinderItemResolution[] | undefined
): RfqImportReviewItemRow[] {
  const byId = new Map((resolutions ?? []).map((r) => [r.itemId, r]));
  return items.map((item, index) => {
    const resolution =
      byId.get(item.id) ??
      (resolutions ?? []).find((r) => r.itemId === String(index + 1)) ??
      (resolutions ?? [])[index];
    const statusDisplay = getPartFinderStatusDisplay(resolution?.status);
    const itemWarnings = resolution?.warnings ?? [];
    return {
      item,
      index,
      resolution,
      statusDisplay,
      itemWarnings,
      reasons: itemWarnings.map(cleanImportReason).filter(Boolean),
    };
  });
}

export function filterImportReviewRows(
  rows: RfqImportReviewItemRow[],
  filter: RfqImportReviewFilter
): RfqImportReviewItemRow[] {
  if (filter === 'matched') {
    return rows.filter((r) => r.statusDisplay.status === 'matched');
  }
  if (filter === 'needs_review') {
    return rows.filter((r) => r.statusDisplay.needsReview);
  }
  return rows;
}

/** Apply is blocked only by hard errors / empty items — never by PartFinder status. */
export function canApplyImportPreview(preview: Pick<RfqExcelImportPreview, 'errors' | 'items'>): boolean {
  return (preview.errors?.length ?? 0) === 0 && (preview.items?.length ?? 0) > 0;
}

export function formatIdpConfidencePercent(overallConfidence: number | undefined): string | null {
  if (overallConfidence == null || Number.isNaN(overallConfidence)) return null;
  const pct =
    overallConfidence <= 1 ? Math.round(overallConfidence * 100) : Math.round(overallConfidence);
  if (pct < 0) return null;
  return `${pct}%`;
}

export function deriveManualReviewRecommended(
  preview: Pick<
    RfqExcelImportPreview,
    'needsManualReview' | 'partfinderResolutions' | 'warnings' | 'errors'
  >
): boolean {
  if (preview.needsManualReview) return true;
  const pf = buildPartFinderReviewSummary(preview.partfinderResolutions);
  if (pf.manualReviewRecommended) return true;
  return (preview.warnings?.length ?? 0) > 0 || (preview.errors?.length ?? 0) > 0;
}

// ——— Unified import review model (single status system for the review UI) ———

export type RfqImportFieldStatus =
  | 'NOT_PROVIDED'
  | 'EXTRACTED'
  | 'MATCHED'
  | 'UNRESOLVED'
  | 'INVALID';

export type RfqImportPartFinderStatus = 'MATCHED' | 'UNRESOLVED' | 'AMBIGUOUS' | 'NOT_SEARCHED';

export function toImportPartFinderStatus(
  status: RfqPartFinderResolveStatus | undefined
): RfqImportPartFinderStatus {
  switch (status) {
    case 'matched':
      return 'MATCHED';
    case 'unresolved':
      return 'UNRESOLVED';
    case 'ambiguous':
      return 'AMBIGUOUS';
    default:
      return 'NOT_SEARCHED';
  }
}

const PLACEHOLDER_DISPLAY_VALUES = new Set(['', '-', '–', '—', 'n/a', 'na', 'null', 'undefined']);

/** Placeholder tokens ("-", "N/A", "null"…) are treated as not provided — never replaced. */
export function cleanImportDisplayValue(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  if (PLACEHOLDER_DISPLAY_VALUES.has(text.toLowerCase())) return null;
  return text;
}

const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/** Strip "Item N:" prefixes and any internal UUIDs from a resolver/parser message. */
export function cleanImportReason(message: string): string {
  return String(message ?? '')
    .replace(/^Item (?!row\b)[^:]+:\s*/i, '')
    .replace(UUID_PATTERN, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export type RfqImportReviewFieldKey =
  | 'rfqNumber'
  | 'quoteDeadline'
  | 'department'
  | 'vessel'
  | 'category'
  | 'subCategory'
  | 'brand'
  | 'model'
  | 'vendors'
  | 'port'
  | 'portAgent';

interface ReviewFieldDef {
  key: RfqImportReviewFieldKey;
  label: string;
  isMasterField: boolean;
  required: boolean;
  /** Optional fields are listed only when the document provides them. */
  showWhenAbsent: boolean;
  dependsOn?: string;
}

const REVIEW_FIELD_DEFS: ReviewFieldDef[] = [
  { key: 'rfqNumber', label: 'RFQ Number', isMasterField: false, required: false, showWhenAbsent: true },
  { key: 'quoteDeadline', label: 'Quote Deadline', isMasterField: false, required: true, showWhenAbsent: true },
  { key: 'department', label: 'Department', isMasterField: true, required: true, showWhenAbsent: true },
  { key: 'vessel', label: 'Vessel', isMasterField: true, required: true, showWhenAbsent: true },
  { key: 'category', label: 'Category', isMasterField: true, required: true, showWhenAbsent: true },
  { key: 'subCategory', label: 'Sub Category', isMasterField: true, required: false, showWhenAbsent: false, dependsOn: 'Category' },
  { key: 'brand', label: 'Brand', isMasterField: true, required: true, showWhenAbsent: true, dependsOn: 'Category' },
  { key: 'model', label: 'Model', isMasterField: true, required: true, showWhenAbsent: true, dependsOn: 'Brand' },
  { key: 'vendors', label: 'Vendors', isMasterField: true, required: true, showWhenAbsent: true },
  { key: 'port', label: 'Port', isMasterField: true, required: false, showWhenAbsent: false },
  { key: 'portAgent', label: 'Port Agent', isMasterField: true, required: false, showWhenAbsent: false, dependsOn: 'Port' },
];

export interface RfqImportFieldReview {
  key: RfqImportReviewFieldKey;
  label: string;
  status: RfqImportFieldStatus;
  /** Original document value (cleaned). Null only when the document has no value. */
  sourceValue: string | null;
  /** ERP master value, shown only when it differs from the source text. */
  erpValue: string | null;
  isMasterField: boolean;
  required: boolean;
  note: string | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function sameText(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function buildFieldReview(
  def: ReviewFieldDef,
  preview: Pick<RfqExcelImportPreview, 'formPatch' | 'resolutions' | 'extractedFields'>
): RfqImportFieldReview {
  const entries = (preview.resolutions ?? []).filter((r) => r.field === def.key);
  const fromExtract = cleanImportDisplayValue(preview.extractedFields?.[def.key]);
  const fromResolutions = entries
    .map((r) => cleanImportDisplayValue(r.importedValue))
    .filter((v): v is string => Boolean(v))
    .join(', ');
  const sourceValue = fromExtract ?? (fromResolutions || null);

  const base = {
    key: def.key,
    label: def.label,
    sourceValue,
    erpValue: null,
    isMasterField: def.isMasterField,
    required: def.required,
  };

  if (!sourceValue) {
    return { ...base, status: 'NOT_PROVIDED', note: 'Not provided in document' };
  }

  if (def.key === 'rfqNumber') {
    return { ...base, status: 'EXTRACTED', note: 'Assigned by the system on create' };
  }

  if (def.key === 'quoteDeadline') {
    const applied = String(preview.formPatch?.quoteDeadline ?? '').trim();
    return ISO_DATE.test(applied)
      ? { ...base, status: 'EXTRACTED', erpValue: sameText(applied, sourceValue) ? null : applied, note: null }
      : { ...base, status: 'INVALID', note: 'Date format not recognized' };
  }

  if (entries.length === 0) {
    return { ...base, status: 'EXTRACTED', note: null };
  }

  const matched = entries.filter((r) => r.status === 'matched');
  const failed = entries.filter(
    (r) => r.status === 'unresolved' || r.status === 'dependency_unresolved'
  );

  if (failed.length === 0) {
    if (matched.length === 0) return { ...base, status: 'EXTRACTED', note: null };
    const erp = matched
      .map((r) => r.resolvedValue ?? '')
      .filter(Boolean)
      .join(', ');
    return {
      ...base,
      status: 'MATCHED',
      erpValue: erp && !sameText(erp, sourceValue) ? erp : null,
      note: null,
    };
  }

  let note = 'Not found in ERP master';
  if (def.key === 'vendors' && matched.length > 0) {
    note = `${matched.length} of ${entries.length} matched · not found: ${failed
      .map((r) => r.importedValue)
      .join(', ')}`;
  } else if (failed.some((r) => r.status === 'dependency_unresolved') && def.dependsOn) {
    note = `Needs ${def.dependsOn} to be matched first`;
  } else if (def.key === 'department') {
    note = 'Not a selectable department';
  }

  return { ...base, status: 'UNRESOLVED', note };
}

export type RfqImportIssueGroup =
  | 'rfq_details'
  | 'master_data'
  | 'item_data'
  | 'partfinder'
  | 'document';

export const RFQ_IMPORT_ISSUE_GROUPS: Array<{ id: RfqImportIssueGroup; label: string }> = [
  { id: 'rfq_details', label: 'RFQ Details' },
  { id: 'master_data', label: 'Master Data' },
  { id: 'item_data', label: 'Item Data' },
  { id: 'partfinder', label: 'PartFinder' },
  { id: 'document', label: 'Document' },
];

export interface RfqImportReviewIssue {
  group: RfqImportIssueGroup;
  message: string;
}

/** Warnings already represented by structured field / PartFinder status. */
const STRUCTURED_WARNING_PATTERNS: RegExp[] = [
  /^(Quote Deadline|Department|Category|Brand|Model|Vendors): (Not provided in source|Provided ")/,
  /^Unsupported quotation field:/,
  /^Vessel ".*" not found in fleet/,
  /^RFQ Number ".*" was recognized/,
];

function countLabel(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

export interface RfqImportReviewModel {
  sourceType: 'Excel' | 'PDF / OCR';
  canApply: boolean;
  statusLabel: string;
  confidenceLabel: string | null;
  fields: RfqImportFieldReview[];
  rows: RfqImportReviewItemRow[];
  issues: RfqImportReviewIssue[];
  counts: {
    itemsImported: number;
    erpFieldsMatched: number;
    erpFieldsChecked: number;
    needsReview: number;
    partFinderMatches: number;
    partFinderChecked: number;
  };
  /** Offer/quotation columns present in the source but outside the RFQ item model. */
  notImportedColumns: string[];
  /** Document labels the parser did not recognize (Excel only). */
  unrecognizedLabels: string[];
}

export function buildRfqImportReviewModel(preview: RfqExcelImportPreview): RfqImportReviewModel {
  const fields = REVIEW_FIELD_DEFS.map((def) => buildFieldReview(def, preview)).filter(
    (f, i) => REVIEW_FIELD_DEFS[i].showWhenAbsent || f.status !== 'NOT_PROVIDED'
  );

  const rows = buildImportReviewItemRows(preview.items ?? [], preview.partfinderResolutions);
  const pfResolutions = preview.partfinderResolutions ?? [];
  const issues: RfqImportReviewIssue[] = [];
  const seen = new Set<string>();
  const addIssue = (group: RfqImportIssueGroup, message: string) => {
    const key = `${group}:${message}`;
    if (!message || seen.has(key)) return;
    seen.add(key);
    issues.push({ group, message });
  };

  for (const f of fields) {
    if (f.status === 'UNRESOLVED') {
      addIssue('master_data', `${f.label} "${f.sourceValue}" — ${f.note}`);
    } else if (f.status === 'INVALID') {
      addIssue('rfq_details', `${f.label} "${f.sourceValue}" — ${f.note}`);
    }
  }
  const missingRequired = fields.filter((f) => f.required && f.status === 'NOT_PROVIDED');
  if (missingRequired.length > 0) {
    addIssue(
      'rfq_details',
      `Not provided in document: ${missingRequired.map((f) => f.label).join(', ')} — complete on the form`
    );
  }

  const structured = new Set<string>([
    ...(preview.resolutions ?? []).map((r) => r.message ?? '').filter(Boolean),
    ...pfResolutions.flatMap((r) => r.warnings ?? []),
  ]);
  for (const warning of preview.warnings ?? []) {
    if (structured.has(warning)) continue;
    if (STRUCTURED_WARNING_PATTERNS.some((p) => p.test(warning))) continue;
    if (/^Item row \d+:/i.test(warning)) {
      addIssue('item_data', cleanImportReason(warning.replace(/^Item row (\d+):\s*/i, 'Row $1: ')));
    } else {
      addIssue('document', cleanImportReason(warning));
    }
  }

  const unresolvedItems = pfResolutions.filter((r) => r.status === 'unresolved').length;
  const ambiguousItems = pfResolutions.filter((r) => r.status === 'ambiguous').length;
  const conflictItems = pfResolutions.filter(
    (r) => r.status === 'matched' && (r.warnings ?? []).some((w) => /different (category|brand|model)/i.test(w))
  ).length;
  if (unresolvedItems > 0) {
    addIssue('partfinder', `${countLabel(unresolvedItems, 'item has', 'items have')} no exact PartFinder match`);
  }
  if (ambiguousItems > 0) {
    addIssue('partfinder', `${countLabel(ambiguousItems, 'item has', 'items have')} multiple PartFinder matches`);
  }
  if (conflictItems > 0) {
    addIssue(
      'partfinder',
      `${countLabel(conflictItems, 'matched item differs', 'matched items differ')} from the RFQ header category, brand or model`
    );
  }

  const canApply = canApplyImportPreview(preview);
  const masterFields = fields.filter((f) => f.isMasterField);
  const knownLabels = new Set(
    [...REVIEW_FIELD_DEFS.map((d) => d.label), 'Vessel Name', 'Port Name'].map((l) => l.toLowerCase())
  );

  return {
    sourceType: preview.source === 'pdf' ? 'PDF / OCR' : 'Excel',
    canApply,
    statusLabel:
      (preview.errors?.length ?? 0) > 0
        ? 'Cannot apply — fix errors'
        : (preview.items?.length ?? 0) === 0
          ? 'No items found'
          : 'Ready to Apply',
    confidenceLabel: formatIdpConfidencePercent(preview.overallConfidence),
    fields,
    rows,
    issues,
    counts: {
      itemsImported: preview.items?.length ?? 0,
      erpFieldsMatched: masterFields.filter((f) => f.status === 'MATCHED').length,
      erpFieldsChecked: masterFields.filter((f) => f.status !== 'NOT_PROVIDED').length,
      needsReview: issues.length,
      partFinderMatches: pfResolutions.filter((r) => r.status === 'matched').length,
      partFinderChecked: pfResolutions.filter((r) => r.status !== 'not_searched').length,
    },
    notImportedColumns: preview.summary?.unsupportedQuotationFields ?? [],
    unrecognizedLabels:
      preview.source === 'pdf'
        ? []
        : (preview.summary?.unmatchedFields ?? []).filter((l) => !knownLabels.has(l.toLowerCase())),
  };
}

/** Source values the ERP could not resolve — kept so Apply never silently drops them. */
export function collectUnresolvedImportValues(
  model: Pick<RfqImportReviewModel, 'fields'>
): Array<{ label: string; value: string; note: string }> {
  return model.fields
    .filter((f) => (f.status === 'UNRESOLVED' || f.status === 'INVALID') && f.sourceValue)
    .map((f) => ({ label: f.label, value: f.sourceValue as string, note: f.note ?? '' }));
}
